import React from "react";
import "react-phone-input-2/lib/style.css";
import {
  TitleBar,
  PrimaryButton,
  Visualization,
  Loading,
  NormalText,
  Modal,
  ModalContent,
} from "components/index";
import { adjustTextMap } from "api/constants";
import { FormNameType, ProcedureType, TScreen, UploadTypeType } from "api/type";
import ScreenContext from "contexts/screen";
import {
  TPostTransformsBatchResponse,
  TPostTransformsResponse,
} from "api/clinicos";

type Screen2Props = {
  batchId: string | null;
  isFormSubmitted: boolean;
  handleSetIsFormSubmittedTrue: (value: boolean) => void;
  selectedProcedure: ProcedureType;
  postTransformsResponse: TPostTransformsResponse | null;
  postTransformsBatchResponse: TPostTransformsBatchResponse | null;
};

const Screen2: React.FC<Screen2Props> = ({
  batchId,
  isFormSubmitted,
  handleSetIsFormSubmittedTrue,
  selectedProcedure,
  postTransformsResponse,
  postTransformsBatchResponse,
}) => {
  // Context
  const setScreen = React.useContext(ScreenContext)[1];

  // State
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const handleSetErrorMessage = (message: string) => {
    setErrorMessage(message);
  };
  // Effects
  React.useEffect(() => {
    if (!isFormSubmitted) {
      setIsModalOpen(true);
    }
  }, [isFormSubmitted]);

  const renderVisualization = () => {
    if (!selectedProcedure) return;
    if (!postTransformsResponse && !postTransformsBatchResponse) {
      return (
        <div
          style={{
            padding: "50px",
          }}
        >
          <Loading />
        </div>
      );
    }
    if (postTransformsResponse) {
      if (!postTransformsResponse.metadata.success) {
        return (
          <NormalText textContent={postTransformsResponse.metadata.reason} />
        );
      }
      return (
        <Visualization
          afterImageUrls={postTransformsResponse.download_urls}
          procedureDisplayMessage={adjustTextMap[selectedProcedure]}
          metaData={postTransformsResponse.metadata}
        />
      );
    }
    if (postTransformsBatchResponse) {
      if (!postTransformsBatchResponse.response[0].metadata.success) {
        return (
          <NormalText
            textContent={
              postTransformsBatchResponse.response[0].metadata.reason
            }
          />
        );
      }
      return (
        <Visualization
          afterImageUrls={
            postTransformsBatchResponse.response[0].presigned_urls
          }
          procedureDisplayMessage={adjustTextMap[selectedProcedure]}
          metaData={postTransformsBatchResponse.response[0].metadata}
        />
      );
    }
  };

  if (errorMessage) return <NormalText textContent={errorMessage} />;
  if (selectedProcedure === null) return null;

  return (
    <div className="screen-container">
      <TitleBar
        name={"Post Op Visualization"}
        disableBackButton={
          selectedProcedure &&
          !postTransformsResponse &&
          !postTransformsBatchResponse
        }
        onClickBackButton={() => setScreen(TScreen.SELECT_MODEL_SCREEN)}
      />
      <div style={screenStyles.visualizationContainer}>
        {renderVisualization()}
      </div>
      <PrimaryButton
        disabled={
          selectedProcedure &&
          !postTransformsResponse &&
          !postTransformsBatchResponse
        }
        onClick={() => setScreen(TScreen.SELECT_MODEL_SCREEN)}
        fullWidth={true}
        text={"Try on another model"}
      />
      <Modal isOpen={isModalOpen}>
        <ModalContent
          batchId={batchId}
          selectedProcedure={selectedProcedure}
          handleCloseModal={() => {
            setIsModalOpen(false);
          }}
          handleSetErrorMessage={handleSetErrorMessage}
          handleSetIsFormSubmittedTrue={handleSetIsFormSubmittedTrue}
          formName={FormNameType.ONBOARDING_ADVERTISE}
          uploadType={batchId ? UploadTypeType.UPLOAD : UploadTypeType.MODEL}
        />
      </Modal>
    </div>
  );
};

const screenStyles = {
  container: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "flex-start",
    height: "100%",
  },
  visualizationContainer: {
    width: "80%",
    height: "100%",
    padding: "10px 0px",
    marginLeft: "auto",
    marginRight: "auto",
    marginBottom: "auto",
  },
  loading: {
    marginBottom: "200px",
  },
  errorText: {
    color: "red",
    textAlign: "center",
    margin: "50px auto",
  },
  spacer: {
    marginBottom: 10,
    marginTop: "auto",
  },
  spacerNoMargin: {
    marginBottom: 10,
  },
} as const;

export default Screen2;
